import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BarChart3, ChevronRight, Loader2, User } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardSubtitle, CardBody } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { useClient, useClientNames } from '@/api/clients';
import { useClientPrograms, type ClientProgram } from '@/api/clientPrograms';
import {
    useClientProgramTarget,
    useClientProgramTargets,
    type ClientProgramTarget,
} from '@/api/clientProgramTargets';
import { useTargetDataPoints } from '@/api/targetDataPoints';
import {
    getDefaultGraphConfig,
    useResetGraphConfig,
    useSaveGraphConfig,
    useTargetGraphConfig,
    type GraphConfig,
} from '@/api/targetGraphConfigs';
import { TargetGraph } from './TargetGraph';
import { GraphConfigPanel } from './GraphConfigPanel';

// ── /data-analysis[/:clientId][?targetId=…] ───────────────────────────────────
// MVP analytics surface: pick a client → browse their active programs + targets
// on the left → graph + config on the right. Programs tab deep-links feed the
// same URL shape.
export function AnalyticsPage() {
    const { clientId } = useParams<{ clientId?: string }>();
    const navigate = useNavigate();

    // No client picked → show the picker.
    if (!clientId) return <ClientPicker onPick={(id) => navigate(`/data-analysis/${id}`)} />;

    return <AnalyticsWorkspace clientId={clientId} />;
}

// ── Client picker ────────────────────────────────────────────────────────────
function ClientPicker({ onPick }: { onPick: (id: string) => void }) {
    const { data: clients, isLoading } = useClientNames();
    const [search, setSearch] = useState('');
    const active = useMemo(
        () => (clients ?? []).filter((c) => c.status === 'Active'),
        [clients],
    );
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return active;
        return active.filter((c) =>
            `${c.first} ${c.last}`.toLowerCase().includes(q)
            || c.last.toLowerCase().includes(q),
        );
    }, [active, search]);

    return (
        <div className="p-6 max-w-[900px] mx-auto">
            <div className="mb-1">
                <h1 className="font-display text-2xl font-bold text-text">Data Analysis</h1>
            </div>
            <p className="text-xs text-text-subtle mb-5">
                Pick a client to graph their program data.
            </p>

            <Card>
                <CardHeader>
                    <div>
                        <CardTitle>Clients</CardTitle>
                        <CardSubtitle>Active clients only</CardSubtitle>
                    </div>
                </CardHeader>
                <CardBody>
                    <input
                        type="search"
                        placeholder="Search by name…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full border border-border rounded-md px-3 py-1.5 text-sm bg-surface mb-3"
                    />
                    {isLoading ? (
                        <div className="py-8 flex items-center justify-center text-text-subtle text-sm">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading clients…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="py-8 text-center text-text-subtle text-sm">No active clients match.</div>
                    ) : (
                        <div className="divide-y divide-border border border-border rounded-md overflow-hidden">
                            {filtered.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => onPick(c.id)}
                                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-surface-muted transition-colors"
                                >
                                    <span className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-text-subtle" />
                                        <span className="font-medium text-text">{c.first} {c.last}</span>
                                    </span>
                                    <ChevronRight className="w-4 h-4 text-text-subtle" />
                                </button>
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
}

// ── Main workspace ───────────────────────────────────────────────────────────
function AnalyticsWorkspace({ clientId }: { clientId: string }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const targetId = searchParams.get('targetId');

    const { data: client, isLoading: loadingClient } = useClient(clientId);

    const selectTarget = (id: string) => {
        const next = new URLSearchParams(searchParams);
        next.set('targetId', id);
        setSearchParams(next, { replace: false });
    };

    return (
        <div className="p-6 max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="flex items-center gap-2 text-xs text-text-subtle mb-0.5">
                        <Link to="/data-analysis" className="hover:underline">Data Analysis</Link>
                        <ChevronRight className="w-3 h-3" />
                        <span>{loadingClient ? '…' : client ? `${client.first} ${client.last}` : 'Unknown client'}</span>
                    </div>
                    <h1 className="font-display text-2xl font-bold text-text flex items-center gap-2">
                        <BarChart3 className="w-6 h-6 text-text-subtle" />
                        {loadingClient ? 'Loading…' : client ? `${client.first} ${client.last}` : 'Client'}
                    </h1>
                </div>
                <Link
                    to="/data-analysis"
                    className="text-xs text-text-muted hover:text-text underline-offset-2 hover:underline"
                >
                    Change client
                </Link>
            </div>

            <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 lg:col-span-4 xl:col-span-3">
                    <ProgramsSidebar
                        clientId={clientId}
                        selectedTargetId={targetId}
                        onSelect={selectTarget}
                    />
                </div>

                <div className="col-span-12 lg:col-span-8 xl:col-span-9">
                    {targetId ? (
                        <TargetAnalysis targetId={targetId} />
                    ) : (
                        <Card>
                            <CardBody>
                                <div className="py-16 text-center text-text-subtle text-sm">
                                    Pick a target on the left to see its graph.
                                </div>
                            </CardBody>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Left sidebar: programs + their targets ──────────────────────────────────
function ProgramsSidebar({
    clientId,
    selectedTargetId,
    onSelect,
}: {
    clientId: string;
    selectedTargetId: string | null;
    onSelect: (id: string) => void;
}) {
    const { data: programs, isLoading } = useClientPrograms({ clientId, includeRetired: true });
    const grouped = useMemo(() => {
        if (!programs) return [];
        // Active programs first, then the rest, each group ordered by name.
        const active = programs.filter((p) => p.status === 'Active');
        const rest = programs.filter((p) => p.status !== 'Active');
        return [...active, ...rest].sort((a, b) => {
            if ((a.status === 'Active') !== (b.status === 'Active')) {
                return a.status === 'Active' ? -1 : 1;
            }
            return a.name.localeCompare(b.name);
        });
    }, [programs]);

    return (
        <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
                <div className="text-xs font-bold uppercase tracking-wider text-text-muted">Programs</div>
            </div>
            {isLoading ? (
                <div className="py-8 flex items-center justify-center text-text-subtle text-xs">
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> Loading…
                </div>
            ) : grouped.length === 0 ? (
                <div className="py-8 text-center text-text-subtle text-xs">No programs.</div>
            ) : (
                <div>
                    {grouped.map((p) => (
                        <ProgramGroup
                            key={p.id}
                            program={p}
                            selectedTargetId={selectedTargetId}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            )}
        </Card>
    );
}

function ProgramGroup({
    program,
    selectedTargetId,
    onSelect,
}: {
    program: ClientProgram;
    selectedTargetId: string | null;
    onSelect: (id: string) => void;
}) {
    const { data: targets, isLoading } = useClientProgramTargets({
        clientProgramId: program.id,
        includeRetired: true,
    });
    const containsSelected = !!targets?.some((t) => t.id === selectedTargetId);
    const [open, setOpen] = useState(program.status === 'Active' || containsSelected);
    // Auto-open a collapsed group when the selection moves into it.
    useEffect(() => {
        if (containsSelected) setOpen(true);
    }, [containsSelected]);

    const sorted = useMemo(() => {
        if (!targets) return [];
        return [...targets].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    }, [targets]);

    return (
        <div className="border-b border-border last:border-b-0">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-surface-muted transition-colors"
            >
                <span className="flex items-center gap-2 min-w-0">
                    <ChevronRight
                        className={`w-3.5 h-3.5 text-text-subtle shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
                    />
                    <span className="text-sm font-medium text-text truncate">{program.name}</span>
                </span>
                <StatusBadge status={program.status} />
            </button>
            {open && (
                <div className="pl-7 pr-2 pb-2">
                    {isLoading ? (
                        <div className="py-3 text-text-subtle text-xs flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" /> Loading targets…
                        </div>
                    ) : sorted.length === 0 ? (
                        <div className="py-2 text-text-subtle text-xs">No targets.</div>
                    ) : (
                        <div className="space-y-0.5">
                            {sorted.map((t) => (
                                <TargetRow
                                    key={t.id}
                                    target={t}
                                    selected={t.id === selectedTargetId}
                                    onSelect={() => onSelect(t.id)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function TargetRow({
    target, selected, onSelect,
}: {
    target: ClientProgramTarget;
    selected: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onSelect}
            className={`w-full flex items-center justify-between gap-2 text-left px-2 py-1 rounded-md text-xs transition-colors ${
                selected
                    ? 'bg-surface-muted text-text font-semibold'
                    : 'text-text-muted hover:bg-surface-muted hover:text-text'
            }`}
        >
            <span className="truncate">{target.name}</span>
            <span className="text-2xs text-text-subtle whitespace-nowrap">{target.dataType}</span>
        </button>
    );
}

// ── Right pane: graph + config ───────────────────────────────────────────────
function TargetAnalysis({ targetId }: { targetId: string }) {
    const { data: points, isLoading: loadingPoints, isError: pointsError } =
        useTargetDataPoints({ targetId });
    const { data: target, isLoading: loadingTarget, isError: targetError } =
        useClientProgramTarget(targetId);
    const { data: savedConfig, isLoading: loadingConfig } = useTargetGraphConfig(targetId);
    const saveMut = useSaveGraphConfig();
    const resetMut = useResetGraphConfig();

    const baseConfig = savedConfig?.config ?? getDefaultGraphConfig(target?.dataType ?? 'percent');
    const [config, setConfig] = useState<GraphConfig>(baseConfig);

    // Reseed local state whenever the active target or its saved config
    // changes — otherwise clicking a new target leaves the old config visible
    // until next render. Also reseeds once target.dataType lands so
    // percent-vs-frequency defaults apply.
    useEffect(() => {
        setConfig(baseConfig);
    }, [targetId, savedConfig?.updatedAt, target?.dataType]); // eslint-disable-line react-hooks/exhaustive-deps

    const dirty = useMemo(
        () => JSON.stringify(config) !== JSON.stringify(baseConfig),
        [config, baseConfig],
    );

    if (loadingPoints || loadingConfig || loadingTarget) {
        return (
            <Card>
                <CardBody>
                    <div className="py-16 flex items-center justify-center text-text-subtle text-sm">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                </CardBody>
            </Card>
        );
    }
    if (pointsError || targetError || !target) {
        return (
            <Card>
                <CardBody>
                    <div className="py-8 text-center text-error text-sm">Couldn't load target data.</div>
                </CardBody>
            </Card>
        );
    }

    return (
        <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12 xl:col-span-9">
                <Card>
                    <CardHeader>
                        <div>
                            <CardTitle>{target.name}</CardTitle>
                            <CardSubtitle>
                                {target.dataType}
                                {target.masteryValue != null && (
                                    <> · Mastery at {target.masteryValue}{target.dataType === 'duration' ? 's' : target.dataType === 'frequency' ? '' : '%'}</>
                                )}
                            </CardSubtitle>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <TargetGraph target={target} points={points ?? []} config={config} />
                    </CardBody>
                </Card>
            </div>
            <div className="col-span-12 xl:col-span-3">
                <Card>
                    <CardHeader>
                        <CardTitle>Display</CardTitle>
                    </CardHeader>
                    <CardBody>
                        <GraphConfigPanel
                            config={config}
                            onChange={setConfig}
                            onSave={() => saveMut.mutate({ targetId, config })}
                            onReset={() => resetMut.mutate(targetId)}
                            saving={saveMut.isPending}
                            resetting={resetMut.isPending}
                            dirty={dirty}
                            hasSavedConfig={!!savedConfig}
                        />
                    </CardBody>
                </Card>
            </div>
        </div>
    );
}

